import { useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  convertFormDataToRequest,
  convertResponseToFormData,
} from '@pages/MapCreateOrEdit/helpers/dataConverter'
import {
  AddPointGroupDialogDataProps,
  MapFormValues,
  MapSettingsDialogDataProps,
  MapSettingsFormValues,
  PointGroupFormValues,
} from '@pages/MapCreateOrEdit/types'

import { useCreateOrUpdateMapConfigMutation, useFetchAllObjectQuery } from '@redux/api'

import { usePrompt } from '@hooks'
import { MODAL_TYPE, ROUTES } from '@constants'
import { AutocompleteOption, ModalType } from '@types'

type Props = {
  methods: UseFormReturn<MapFormValues>
  setShowAddPointGroupDialog: (show: boolean) => void
  setAddPointGroupDialogData: (data: AddPointGroupDialogDataProps | undefined) => void
}

export const useHandlers = (props: Props) => {
  const { methods, setShowAddPointGroupDialog, setAddPointGroupDialogData } = props

  const { t } = useTranslation()

  const navigate = useNavigate()

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = methods

  usePrompt({ when: isDirty })

  const watchPointGroups = watch('pointGroups')
  const watchId = watch('id')
  const watchCode = watch('code')

  const { data: objects } = useFetchAllObjectQuery()

  const [mapSettingsDialogData, setMapSettingsDialogData] = useState<
    MapSettingsDialogDataProps | undefined
  >()

  const objectsOptions: AutocompleteOption[] = useMemo(
    () => objects?.map(obj => ({ id: obj.code, label: obj.title })) || [],
    [objects]
  )

  const [createOrUpdateMapConfig, { isLoading: isCreating }] = useCreateOrUpdateMapConfigMutation()

  const handleShowAddPointGroupDialog = (modalType: ModalType, id?: string | number) => {
    if (modalType === MODAL_TYPE.CREATE) {
      setShowAddPointGroupDialog(true)
      setAddPointGroupDialogData({
        modalType,
      })

      return
    }

    const pointGroup = watchPointGroups?.find(item => item.id === id)

    if (pointGroup) {
      setShowAddPointGroupDialog(true)
      setAddPointGroupDialogData({
        modalType,
        pointGroupData: pointGroup,
      })
    }
  }

  const handleCloseAddPointGroupDialog = (isDirty?: boolean) => {
    if (!isDirty) {
      setShowAddPointGroupDialog(false)
      setAddPointGroupDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setShowAddPointGroupDialog(false)
      setAddPointGroupDialogData(undefined)
    }
  }

  const handleDeletePointGroup = (id: string) => {
    setValue(
      'pointGroups',
      watchPointGroups.filter(pointGroup => pointGroup.id !== id),
      { shouldDirty: true }
    )
  }

  const handleSaveMap = handleSubmit(data => {
    const requestData = convertFormDataToRequest(data)

    createOrUpdateMapConfig(requestData)
      .unwrap()
      .then(resp => {
        const formData = convertResponseToFormData(resp)

        reset(formData)
      })
  })

  const handleCancelMap = () => {
    if (!isDirty || confirm(t('notifications.leave'))) {
      navigate(ROUTES.MAPS)
    }
  }

  const handleUpdatePointGroups = (props: {
    pointGroup: PointGroupFormValues
    modalType: ModalType
  }) => {
    const { modalType, pointGroup } = props

    if (modalType === MODAL_TYPE.CREATE) {
      setValue('pointGroups', [...watchPointGroups, pointGroup], { shouldDirty: true })

      handleCloseAddPointGroupDialog(false)

      return
    }

    const index = watchPointGroups.findIndex(pg => pg.id === pointGroup.id)

    if (index !== -1) {
      const newPointGroups = [...watchPointGroups]
      newPointGroups[index] = pointGroup
      setValue('pointGroups', newPointGroups, { shouldDirty: true })
    }

    handleCloseAddPointGroupDialog(false)
  }

  const handleOpenMapSettingsDialog = () => {
    const currentMapSettings = watch('mapSettings')
    setMapSettingsDialogData({
      mapSettings: currentMapSettings,
    })
  }

  const handleCloseMapSettingsDialog = (isDirtyDialog?: boolean) => {
    if (!isDirtyDialog) {
      setMapSettingsDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setMapSettingsDialogData(undefined)
    }
  }

  const handleUpdateMapSettings = (mapSettings: MapSettingsFormValues) => {
    setValue('mapSettings', mapSettings, { shouldDirty: true })
    setMapSettingsDialogData(undefined)
  }

  return {
    state: {
      methods,
      isDirty,
      isCreating,
      objectsOptions,
      mapSettingsDialogData,
    },
    data: {
      pointGroups: watchPointGroups,
      mapId: watchId,
      mapCode: watchCode,
    },
    handlers: {
      handleSaveMap,
      handleCancelMap,
      handleDeletePointGroup,
      handleUpdatePointGroups,
      handleOpenMapSettingsDialog,
      handleCloseMapSettingsDialog,
      handleUpdateMapSettings,
      handleShowAddPointGroupDialog,
      handleCloseAddPointGroupDialog,
    },
  }
}
